import { selector } from "recoil";

import { NO_VALUE_DISPLAY } from "../../constants";
import { AuthorizationSet, RoleDataItem } from "../../dto";
import {
  groupByAuthorizedIn,
  hasKeys,
  sortProfileCompanies,
} from "../../helpers";
import { IProfileListCompanyDto } from "../../models";
import {
  isSelectCompanyOrRoleButtonActiveAtom,
  profileDtoAtom,
  profileScopeAtom,
  subdomainIsolationCompanyIdAtom,
} from "../atoms";

export const companyIdSelector = selector<number | undefined>({
  key: "companyIdSelector",
  get: ({ get }) => {
    const profileScope = get(profileScopeAtom);
    return profileScope?.companyId;
  },
});
export const currentUserIdSelector = selector<number | undefined>({
  key: "currentUserIdSelector",
  get: ({ get }) => {
    const profileDto = get(profileDtoAtom);
    return profileDto?.id;
  },
});

export const profilePermissionsSelector = selector<RoleDataItem | undefined>({
  key: "profilePermissionsSelector",
  get: ({ get }) => {
    const profileDto = get(profileDtoAtom);
    const profileScope = get(profileScopeAtom);

    if (
      !profileDto ||
      !hasKeys(profileDto?.roleData) ||
      !profileScope?.roleId
    ) {
      return undefined;
    }

    return profileDto.roleData[profileScope.roleId];
  },
});

export const profileMetadataLanguageSelector = selector<string | undefined>({
  key: "profileMetadataLanguageSelector",
  get: ({ get }) => {
    const profileData = get(profileDtoAtom);
    return profileData?.metadata?.language;
  },
});

export const profileAuthorizedGroupedInSelector = selector<
  Partial<Record<number, AuthorizationSet[]>> | undefined
>({
  key: "profileAuthorizedGroupedInSelector",
  get: ({ get }) => {
    const profileDto = get(profileDtoAtom);
    return groupByAuthorizedIn(profileDto);
  },
});

export const profileCompaniesDtoSelector = selector<IProfileListCompanyDto[]>({
  key: "profileCompaniesDtoSelector",
  get: ({ get }) => {
    const groupedData = get(profileAuthorizedGroupedInSelector);

    if (!groupedData || !hasKeys(groupedData)) {
      return [];
    }

    let companies: IProfileListCompanyDto[] = Object.entries(groupedData).map(
      ([companyId, authSets]) => {
        return {
          id: +companyId,
          title: authSets?.[0]?.companyName || NO_VALUE_DISPLAY,
          authorizeSets: authSets || [],
        };
      },
    );

    const subdomainIsolationCompanyId = get(subdomainIsolationCompanyIdAtom);

    if (subdomainIsolationCompanyId) {
      companies = companies.filter(
        company => company.id === subdomainIsolationCompanyId,
      );
    }

    return sortProfileCompanies(companies);
  },
});

export const isProfileScopeLoadedSelector = selector({
  key: "isProfileScopeLoadedSelector",
  get: ({ get }) => {
    const profileScope = get(profileScopeAtom);

    return profileScope !== undefined;
  },
});

export const isCompanySelectDialogAvailableSelector = selector({
  key: "isCompanySelectDialogAvailableSelector",
  get: ({ get }) => {
    const companies = get(profileCompaniesDtoSelector);
    const isSelectCompanyOrRoleSwitchActive = get(
      isSelectCompanyOrRoleButtonActiveAtom,
    );

    return isSelectCompanyOrRoleSwitchActive && companies.length > 0;
  },
});
